import { useCallback, useEffect, useRef } from 'react';
import { useLocalStorage } from 'services/hooks';
import { ModelView, ModelViewsSelectorProps } from './ModelViewsSelector.props';
import { MainContainer, ViewButtonContainer, ViewsContainer } from './ModelViewsSelector.styles';
import ViewButtonWithTooltip from './ViewButtonWithTooltip';
import { ACTIVE_MODEL_VIEW_KEY, MODELS_VIEWS_KEY } from '../common/constants';



const ModelViewsSelector = ({ onLoadView, currentViewId, views: externalViews }: ModelViewsSelectorProps) => {
  const [internalViews] = useLocalStorage<ModelView[]>(MODELS_VIEWS_KEY, []);
  const [activeModelView, setActiveModelView] = useLocalStorage<ModelView | null>(ACTIVE_MODEL_VIEW_KEY, null);
  const views = externalViews ?? internalViews;
  const hasLoadedActiveView = useRef(false);

  // Automatically load the saved active view when component mounts
  useEffect(() => {
    // Only load if we haven't loaded yet, have an active view saved, and views are available
    if (!hasLoadedActiveView.current && activeModelView && views.length > 0) {
      // Check if the active view still exists in the views array
      const viewExists = views.some(view => view.id === activeModelView.id);
      if (viewExists) {
        // Only load if the current view is different from the active view
        // This prevents unnecessary reloading if the view is already active
        // Also check if currentViewId is the default - if so, we should load the active view
        if (currentViewId !== activeModelView.id && (currentViewId === 'default' || !currentViewId)) {
          onLoadView(activeModelView);
        }
        hasLoadedActiveView.current = true;
      } else {
        // If the saved view no longer exists, clear it
        setActiveModelView(null);
        hasLoadedActiveView.current = true;
      }
    }
    // Reset the flag if views become empty (e.g., after a reset)
    if (views.length === 0) {
      hasLoadedActiveView.current = false;
    }
  }, [activeModelView, views, onLoadView, setActiveModelView, currentViewId]);

  const handleLoadView = useCallback((event: React.MouseEvent<HTMLButtonElement>, savedView: ModelView) => {
    event.stopPropagation();
    onLoadView(savedView);
    setActiveModelView(savedView);
  }, [onLoadView, setActiveModelView]);

  return (
    <MainContainer sx={{ ml: 1 }}>
      <ViewsContainer>
        {views.map(savedView => (
          <ViewButtonContainer key={savedView.id}>
            <ViewButtonWithTooltip 
              savedView={savedView} 
              currentViewId={currentViewId}
              onClick={handleLoadView}
            />
          </ViewButtonContainer>
        ))}
      </ViewsContainer>
    </MainContainer>
  );
};

export default ModelViewsSelector;